/**
 * This composable is used assist the front end in building pretty urls.
 * Locations can be added as a: slug or a name.
 * A slug is a preset name given for a location by the backend.
 * A name is a text string that is used to search for a location.
 * Slugs are used for pretty urls, names are used for search params.
 * For reference these are the default url segments:
 *  ['search-jobs', 'all-types', 'all-roles', 'location']
 *  ['search-reviews', 'all-types', 'all-roles', 'location']
 *  ['search-companies', 'all-industries', 'location']
 */

export default function useLocationPrettyUrl() {
  const getUrl = () => new URL(window.location.href);
  const getUrlSegments = (url) => url.pathname.split('/').filter((segment) => segment);
  const doesUrlHaveSearchParams = (url) => {
    const paramKeys = [...url.searchParams.keys()].filter((key) => key !== 'page');
    return paramKeys.length > 0;
  };

  const addSlugToUrl = (url, slug) => {
    const urlSegments = getUrlSegments(url);
    const segmentsWithLocation = [...urlSegments];

    if (urlSegments[0] === 'search-companies') {
      switch (urlSegments.length) {
        case 1:
          segmentsWithLocation.push('all-industries', slug);
          break;
        case 2:
          segmentsWithLocation.push(slug);
          break;
        case 3:
          segmentsWithLocation[2] = slug;
          break;
        default:
          break;
      }

      return `/${segmentsWithLocation.join('/')}`;
    }

    switch (urlSegments.length) {
      case 1:
        segmentsWithLocation.push('all-types', 'all-roles', slug);
        break;
      case 2:
        segmentsWithLocation.push('all-roles', slug);
        break;
      case 3:
        segmentsWithLocation.push(slug);
        break;
      case 4:
        segmentsWithLocation[3] = slug;
        break;
      default:
        break;
    }

    return `/${segmentsWithLocation.join('/')}`;
  };

  const addLocationToUrl = (location, radius = 10) => {
    const url = getUrl();
    const urlSegments = getUrlSegments(url);
    let urlHasLocationSlug = false;

    if (urlSegments[0] === 'search-companies') {
      urlHasLocationSlug = urlSegments.length === 3;
    } else {
      urlHasLocationSlug = urlSegments.length === 4;
    }

    // console.log(url);
    // console.log(doesUrlHaveSearchParams(url));
    // console.log(location.slug);

    if (doesUrlHaveSearchParams(url) || location.slug === null) {
      // If the url has a slug location, we need to remove it.
      if (urlHasLocationSlug) {
        if (urlSegments[0] === 'search-companies') {
          url.pathname = urlSegments.slice(0, 2).join('/');
        } else {
          url.pathname = urlSegments.slice(0, 3).join('/');
        }
      }

      url.searchParams.set('location', location.name);

      if (radius !== 10) {
        url.searchParams.set('radius', radius);
      }

      return url;
    }

    url.searchParams.delete('location');
    url.pathname = addSlugToUrl(url, location.slug);

    return url;
  };

  const removeLocationFromUrl = () => {
    const url = getUrl();
    const urlSegments = getUrlSegments(url);

    url.searchParams.delete('location');
    url.searchParams.delete('radius');

    let pathName = (urlSegments[0] === 'search-companies') ? urlSegments.slice(0, 2).join('/') : urlSegments.slice(0, 3).join('/');
    if ((url.searchParams.size === 0) || (url.searchParams.size === 1 && url.searchParams.get('best-employer'))) {
      pathName = pathName.replace('/all-roles', '').replace('/all-types', '').replace('/all-industries', '');
    }
    url.pathname = pathName;
    return url;
  };

  return {
    addLocationToUrl,
    removeLocationFromUrl,
  };
}
